import { CLASS_NAME } from 'constants/class-names'
import { Nullable } from 'constants/types'
import {
    AriaAttr,
    EventType,
    HtmlAttr,
    KeyType,
    HeaderMenuNames,
    HeaderMenuEvent
} from 'constants/enums'
import { ATTR_NAME } from 'constants/attr-name'
import {
    isMobile,
    reduceMotionQueryList,
    isReduceMotion
} from 'utils/system/device'
import { ComponentBase } from 'utils/abstract-classes/component-base'

import { AnimationName } from 'components/header/header-interface'
import { ArrowDirection, MenuItem } from 'components/header/menu-item'
import { HeaderStore } from 'components/header/store'

const {
    header: {
        search: searchClass,
        menu: menuClass,
        mainMenu: mainMenuClass,
        accountMenu: accountMenuClass
    },
    common: commonClass
} = CLASS_NAME

/**
 * Menu
 * The root element controlBtn button,
 * and this controls menu that the control button attribute determines
 * special case: when mobile search elements exist in the menu
 */
export class Menu extends ComponentBase {
    public lastTouchedL1Item?: MenuItem
    protected menu: HTMLElement
    private reduceMotion = false
    private targetMenuName: HeaderMenuNames
    private menuLabel: string
    private lastYposition = 0
    // HTML Elements
    private firstMenuItem: MenuItem
    private skipTo: Nullable<HTMLAnchorElement>
    private mobileSearch?: HTMLInputElement
    private subscribeBtn?: Nullable<HTMLDivElement>
    private breakingBanner?: Nullable<HTMLDivElement>
    private isPromotionalActivated = false
    private isSloganActivated = false
    private isBundleMenuActivated = false

    // eslint-disable-next-line no-magic-numbers
    private readonly HEADER_THRESHOLD = 50

    /**
     * Attach all the event listeners
     * @param {HTMLButtonElement} controlBtn
     */
    public constructor(public controlBtn: HTMLButtonElement) {
        super()

        const target = this.controlBtn.getAttribute(
            ATTR_NAME.header.menu.target
        ) as HeaderMenuNames

        // if the control button has no target menu exit
        if (!target) {
            this.controlBtn.remove()
            return
        }
        this.targetMenuName = target

        // When menu initialization failed, shut down all functionalities.
        this.initMenu()
        if (!this.menu) {
            this.controlBtn.remove()
            return
        }

        this.isPromotionalActivated = this.menu.classList.contains(
            mainMenuClass.promotional
        )
        this.isSloganActivated = this.menu.classList.contains(
            mainMenuClass.slogan
        )

        this.isBundleMenuActivated = this.menu.classList.contains(
            mainMenuClass.bundlMenu
        )

        // Adjust menu position when promotional bar is enabled
        if (
            this.isPromotionalActivated ||
            this.isBundleMenuActivated ||
            this.isSloganActivated
        ) {
            this.handleMenuPosition()
            window.addEventListener(EventType.SCROLL, () =>
                this.handleMenuPosition()
            )
        }

        // Reducing motion will ignore the animation
        // based on setting remove or add the listeners from this.menu
        const motionReducedMQ = reduceMotionQueryList()
        this.handleReduceMotionChanged()
        // ignore TS deprecated here: addListener is part of MediaQueryList Interface
        // https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList
        if (motionReducedMQ.addListener) {
            motionReducedMQ.addListener(this.handleReduceMotionChanged)
        }

        this.initMobileSearchForm()
        this.initMenuItems()

        // menu specific language for aria-label
        // default is generic "menu"
        this.menuLabel =
            this.controlBtn.getAttribute(ATTR_NAME.header.menu.label) || 'Menu'

        this.skipTo = this.getElement<HTMLAnchorElement>(
            `.${commonClass.skipTo}`
        )

        this.breakingBanner = this.getElement<HTMLDivElement>(
            `.${CLASS_NAME.header.breaking}`
        )
        this.subscribeBtn = this.getElement<HTMLDivElement>(
            `.${CLASS_NAME.header.subscribe}[${ATTR_NAME.header.scroll._}]`
        )

        // controlBtn button click: toggle menu
        this.controlBtn.addEventListener(EventType.CLICK, () =>
            this.toggleMenu()
        )

        // controlBtn button key events
        this.controlBtn.addEventListener(
            EventType.KEYDOWN,
            (e: KeyboardEvent) => this.handleControlBtnKeyboardEvents(e)
        )

        // Document event Listeners to close the menu
        document.addEventListener(EventType.CLICK, (e: MouseEvent) =>
            this.attemptToClose(e.target)
        )
        // menu escape key
        this.menu.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
            if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
                this.closeMenu()
                return
            }
        })

        HeaderStore.getInstance().menus[this.targetMenuName] = this
    }

    /**
     * handle menu position by user scroll
     */
    private handleMenuPosition(): void {
        if (window.scrollY >= this.HEADER_THRESHOLD) {
            this.resetMenuPosition()
        } else {
            this.adjustMenuPosition()
        }
    }

    /**
     * reset menu position after header
     * CSS dependency
     */
    private resetMenuPosition(): void {
        if (this.isPromotionalActivated) {
            this.menu.classList.remove(mainMenuClass.promotional)
        }
        if (this.isSloganActivated) {
            this.menu.classList.remove(mainMenuClass.slogan)
        }
        if (this.isBundleMenuActivated) {
            this.menu.classList.remove(mainMenuClass.bundlMenu)
        }
    }

    /**
     * adjust menu position after header
     * CSS dependency
     */
    private adjustMenuPosition(): void {
        if (this.isPromotionalActivated) {
            this.menu.classList.add(mainMenuClass.promotional)
        }
        if (this.isSloganActivated) {
            this.menu.classList.add(mainMenuClass.slogan)
        }
        if (this.isBundleMenuActivated) {
            this.menu.classList.add(mainMenuClass.bundlMenu)
        }
    }

    /**
     * Get the current menu open status
     * @returns {boolean}
     */
    private isMenuOpened(): boolean {
        return (
            document.documentElement.classList.contains(menuClass.opened) &&
            document.documentElement.classList.contains(
                this.targetMenuOpenClass
            )
        )
    }

    /**
     * Initialize menu
     */
    private initMenu(): void {
        const menu = this.getElement(`.${this.targetMenuName}.${menuClass._}`)

        if (!menu) {
            return
        }

        this.menu = menu
    }

    /**
     * Initialize menu items
     * All items are linked by its keyboard events
     */
    private initMenuItems(): void {
        let prevItem: Nullable<MenuItem> = undefined
        let firstL2Item: Nullable<MenuItem> = undefined
        const rootItems: MenuItem[] = []

        // Per <li> elements
        this.getElements<HTMLLIElement>(`.${menuClass.item}`, this.menu).map(
            (item) => {
                const level = parseInt(
                    item.getAttribute(ATTR_NAME.header.menu.level) || '1'
                )
                const menuItem = new MenuItem(item, level, this)
                const prevL1 = rootItems[rootItems.length - 1]

                // All items can be navigated by a tab
                if (prevItem) {
                    prevItem.nextElement = menuItem
                    menuItem.prevElement = prevItem
                }

                // L1
                if (level === 1) {
                    // ⬅️ will be the L1 item even though it's L1 item (self)
                    menuItem.setArrowElement(menuItem, ArrowDirection.Left)
                    // ⬇️ and ⬆️ goes to the prev/next L1 item
                    if (prevL1) {
                        prevL1.setArrowElement(menuItem, ArrowDirection.Down)
                        menuItem.setArrowElement(prevL1, ArrowDirection.Up)
                    }

                    /**
                     * When the current is L1 and the first L2 exists, it means the prev item is the last L2,
                     * so connect the first L2 and the last L2 (prev item) with ⬇️ and ⬆️ to circle around
                     */
                    if (firstL2Item && prevItem) {
                        prevItem.setArrowElement(
                            firstL2Item,
                            ArrowDirection.Down
                        )
                        firstL2Item.setArrowElement(prevItem, ArrowDirection.Up)
                    }

                    rootItems.push(menuItem)
                    firstL2Item = undefined
                }

                // L2
                if (level === 2 && prevItem) {
                    // ⬅️ goes to the L1 item
                    menuItem.setArrowElement(prevL1, ArrowDirection.Left)

                    // This is the first L2 item, so prev item(L1)'s ➡️ is the current one
                    if (!firstL2Item) {
                        firstL2Item = menuItem
                        prevItem.setArrowElement(menuItem, ArrowDirection.Right)
                    } else {
                        // connect the current and prev item with ⬇️ and ⬆️
                        menuItem.setArrowElement(prevItem, ArrowDirection.Up)
                        prevItem.setArrowElement(menuItem, ArrowDirection.Down)
                    }
                }

                // L3
                // eslint-disable-next-line no-magic-numbers
                if (level === 3 && prevItem) {
                    // ⬅️ goes to the L1 item
                    menuItem.setArrowElement(prevL1, ArrowDirection.Left)
                    // connect the current and prev item with ⬇️ and ⬆️
                    menuItem.setArrowElement(prevItem, ArrowDirection.Up)
                    prevItem.setArrowElement(menuItem, ArrowDirection.Down)
                }

                prevItem = menuItem
            }
        )

        // Connect the first and last L1 with ⬇️ and ⬆️
        if (rootItems.length) {
            rootItems[rootItems.length - 1].setArrowElement(
                rootItems[0],
                ArrowDirection.Down
            )
            rootItems[0].setArrowElement(
                rootItems[rootItems.length - 1],
                ArrowDirection.Up
            )
        }

        // Link L1 items for resetting all HTML classes
        rootItems.forEach((item, index) => {
            if (rootItems[index + 1]) {
                item.nextRootItem = rootItems[index + 1]
            }
        })

        this.firstMenuItem = rootItems[0]
    }

    /**
     * Mobile search form
     * if input doesn't exist return
     * Tab goes to the menu, and shift + tab goes to controlBtn
     */
    private initMobileSearchForm(): void {
        const mobileSearch = this.getElement<HTMLInputElement>(
            `.${searchClass._} input`,
            this.menu
        )

        if (!mobileSearch) {
            return
        }

        this.mobileSearch = mobileSearch
        this.mobileSearch.addEventListener(
            EventType.KEYDOWN,
            (e: KeyboardEvent) => {
                if (e.key !== KeyType.TAB) {
                    return
                }
                e.preventDefault()

                if (e.shiftKey) {
                    this.controlBtn.focus()
                } else {
                    this.firstMenuItem.focus()
                }
            }
        )
    }

    /**
     * Reset the menu and menu item HTML classes
     */
    public removeRootItemClasses(): void {
        this.firstMenuItem.removeRootHTMLClasses()
    }

    /**
     * Reset the menu HTML classes
     * @param {string} className [optional] class name
     */
    public setMenuClass(className?: string): void {
        this.menu.classList.remove(
            mainMenuClass.noColumn,
            mainMenuClass.oneColumn,
            mainMenuClass.twoColumn
        )

        if (!className) {
            return
        }
        this.menu.classList.add(className)
    }

    /**
     * Check where the user clicked
     * confirm against rules and return or close the menu
     * @param {Nullable<EventTarget>} target
     */
    private attemptToClose(target: Nullable<EventTarget>): void {
        if (
            !this.isMenuOpened() ||
            this.controlBtn.contains(target as HTMLElement) ||
            this.menu.contains(target as HTMLElement)
        ) {
            return
        }
        // if you click outside the current menu or control button
        this.closeMenu()
    }

    /**
     * Handle when keyboard is used on the controlBtn button
     * @param {KeyboardEvent} e
     */
    private handleControlBtnKeyboardEvents(e: KeyboardEvent): void {
        // When the non-mobile primary menu is closed, tab should focus the desktop search component
        if (
            this.targetMenuName == HeaderMenuNames.PRIMARY &&
            e.key === KeyType.TAB &&
            !e.shiftKey &&
            !this.isMenuOpened() &&
            !isMobile()
        ) {
            e.preventDefault()
            HeaderStore.getInstance().search.focus()
            return
        }

        if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
            this.closeMenu()
            return
        }
    }

    /**
     * Check if menu is open or closed and call the opposite
     */
    private toggleMenu(): void {
        this.isMenuOpened() ? this.closeMenu() : this.openMenu()
    }

    /**
     * call all the logic for opening the menu
     */
    protected openMenu(): void {
        this.lastYposition = window.scrollY
        HeaderStore.getInstance().closeAllMenus()
        document.documentElement.classList.remove(menuClass.closed)
        document.documentElement.classList.add(
            menuClass.opened,
            this.targetMenuOpenClass
        )
        this.removeRootItemClasses()
        this.setMenuClass()

        this.openAnimationStart()
        // hide sticky scrolling elements
        if (isMobile()) {
            window.scrollTo(0, 0)
            this.hideAdjacentStickyElements()
            window.dispatchEvent(new CustomEvent(HeaderMenuEvent.OPEN))
        } else {
            // scroll to top if breaking banner is enabled
            if (
                this.breakingBanner &&
                this.lastYposition < this.HEADER_THRESHOLD
            ) {
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }

        // if reduce motion is true animation start/end events will not fire
        if (this.reduceMotion) {
            this.openAnimationEnd()
        }
    }

    /**
     * call all the logic for closing the menu
     */
    public closeMenu(): void {
        if (isMobile()) {
            window.scrollTo(0, this.lastYposition)
        }
        this.removeRootItemClasses()

        document.documentElement.classList.remove(
            menuClass.opened,
            mainMenuClass.open,
            accountMenuClass.open
        )
        document.documentElement.classList.add(menuClass.closed)

        this.closeAnimationStart()

        // if reduce motion is true animation start/end events will not fire
        if (this.reduceMotion) {
            this.closeAnimationEnd()
        }
    }

    /**
     * Moves focus to the first element in the menu
     * Tablet & Desktop: focus the first menu item
     * Mobile: focus the mobile search if it exists
     */
    private focusFirstItem(): void {
        if (isMobile() && this.mobileSearch) {
            return
        }

        this.firstMenuItem.focus()
        this.lastTouchedL1Item = this.firstMenuItem
    }

    /**
     * based on user preferences add or remove animation listeners
     */
    private handleReduceMotionChanged(): void {
        this.reduceMotion = isReduceMotion()

        if (this.reduceMotion) {
            this.removeAnimationEventListeners()
            return
        }

        this.addAnimationEventListeners()
    }

    /**
     * attach animation listeners for animation start and end
     */
    private addAnimationEventListeners(): void {
        this.menu.addEventListener(
            EventType.ANIMATION_START,
            (e: AnimationEvent) => this.animationStartEventsHandler(e)
        )
        this.menu.addEventListener(
            EventType.ANIMATION_END,
            (e: AnimationEvent) => this.animationEndEventsHandler(e)
        )
    }

    /**
     * remove animation listeners for animation start and end
     */
    private removeAnimationEventListeners(): void {
        this.menu.removeEventListener(
            EventType.ANIMATION_START,
            (e: AnimationEvent) => this.animationStartEventsHandler(e)
        )
        this.menu.removeEventListener(
            EventType.ANIMATION_END,
            (e: AnimationEvent) => this.animationEndEventsHandler(e)
        )
    }

    /**
     * filter animation by the name for the menu animation
     * call the functions that should be run when the animation starts
     * CSS dependency
     *
     * @param {AnimationEvent} e
     */
    private animationStartEventsHandler(e: AnimationEvent): void {
        if (
            e.animationName === AnimationName.SLIDE_UP &&
            e.target === this.menu
        ) {
            this.closeAnimationStart()
        }
    }

    /**
     * filter animation by the name for the menu animation
     * call the functions that should be run when the animations end
     * CSS dependency
     *
     * @param {AnimationEvent} e
     */
    private animationEndEventsHandler(e: AnimationEvent): void {
        if (
            e.animationName === AnimationName.SLIDE_DOWN &&
            e.target === this.menu
        ) {
            this.openAnimationEnd()
            return
        }

        if (
            e.animationName === AnimationName.SLIDE_UP &&
            e.target === this.menu
        ) {
            this.closeAnimationEnd()
        }
    }

    /**
     * remove hidden attr from menu to show/animate it
     */
    private openAnimationStart(): void {
        this.menu.hidden = false
    }

    /**
     * disable tabbing on elements behind the menu once it has opened
     * toggle the aria values to inform the menu is open
     */
    private openAnimationEnd(): void {
        if (this.main) {
            this.main.tabIndex = -1
        }

        if (this.skipTo) {
            this.skipTo.tabIndex = -1
        }

        this.controlBtn.setAttribute(AriaAttr.EXPANDED, 'true')
        this.controlBtn.setAttribute(AriaAttr.LABEL, `Close ${this.menuLabel}`)
        // disable scrolling the website with the menu open
        if (!isMobile()) {
            document.documentElement.classList.add(commonClass.scrollDisable)
        }
        this.focusFirstItem()
    }

    /**
     * enable tabbing on elements behind the menu once it has closed
     * toggle the aria values to inform the menu is closed
     * return focus to the element that opened the menu
     * (fake natural focus order because elements aren't actually next to each other)
     */
    private closeAnimationStart(): void {
        if (this.main) {
            this.main.removeAttribute(HtmlAttr.TAB_INDEX)
        }
        if (this.skipTo) {
            this.skipTo.removeAttribute(HtmlAttr.TAB_INDEX)
        }

        this.controlBtn.setAttribute(AriaAttr.EXPANDED, 'false')
        this.controlBtn.setAttribute(AriaAttr.LABEL, `Open ${this.menuLabel}`)
        // restore scrolling
        document.documentElement.classList.remove(commonClass.scrollDisable)
        this.controlBtn.focus()
    }

    /**
     * add hidden attr to menu to hide it
     */
    private closeAnimationEnd(): void {
        this.menu.hidden = true
        // unhide sticky scroll elements
        // restore scroll depth
        if (isMobile()) {
            this.showAdjacentStickyElements()
            window.dispatchEvent(new CustomEvent(HeaderMenuEvent.CLOSE))
        }
    }

    private get targetMenuOpenClass(): string {
        switch (this.targetMenuName) {
            case HeaderMenuNames.PRIMARY:
                return mainMenuClass.open
            case HeaderMenuNames.ACCOUNT:
                return accountMenuClass.open
            default:
                return menuClass.opened
        }
    }

    /**
     * add hidden attr to all adjacent sticky header elements
     */
    private hideAdjacentStickyElements(): void {
        if (this.subscribeBtn) {
            this.subscribeBtn.hidden = true
        }
        if (this.breakingBanner) {
            this.breakingBanner.hidden = true
        }
    }

    /**
     * remove hidden attr from all adjacent sticky header elements
     */
    private showAdjacentStickyElements(): void {
        if (this.subscribeBtn) {
            this.subscribeBtn.hidden = false
        }
        if (this.breakingBanner) {
            this.breakingBanner.hidden = false
        }
    }
}
